import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../api";

const BASE = "/monitorizacion/notificaciones";

export default function NativeAlertBell({
  onSelectClient = () => {},
  onSelectEquipment = () => {},
  onOpenUser = () => {},
  onCreateAviso = () => {},
  onViewAviso = () => {}
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const response = await api.get(`${BASE}/resumen`);
      setUnread(response.data.unread || 0);
      setError("");
    } catch (err) {
      setError("No se pudieron consultar las notificaciones");
    }
  }, []);

  const loadNotifications = useCallback(async () => {
    setLoading(true);

    try {
      const response = await api.get(BASE);
      setNotifications(response.data.notifications || []);
      setLoaded(true);
      setError("");
    } catch (err) {
      setLoaded(false);
      setError("No se pudieron cargar las notificaciones");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();

    const timer = setInterval(refresh, 60000);

    return () => clearInterval(timer);
  }, [refresh]);

  useEffect(() => {
    if (!open) return;

    const handleMouseDown = (event) => {
      if (containerRef.current &&
          !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const toggle = async () => {
    const next = !open;
    setOpen(next);

    if (next) {
      onOpenUser();
      await loadNotifications();
    }
  };

  const markRead = async (notification) => {
    try {
      await api.post(
        `${BASE}/${encodeURIComponent(notification.event_id)}/leer`
      );

      setNotifications(current =>
        current
          .map(item =>
            item.event_id === notification.event_id
              ? { ...item, is_read: true }
              : item
          )
          .sort((a, b) =>
            Number(a.is_read) - Number(b.is_read) ||
            new Date(b.created_at) - new Date(a.created_at)
          )
      );

      await refresh();
    } catch (err) {
      setError("No se pudo marcar la notificación como leída");
    }
  };

  const alertLabel = (type) => ({
    DISK_USAGE: "Espacio insuficiente en disco",
    AGENT_OFFLINE: "Equipo sin comunicación"
  }[type] || type || "Alerta de monitorización");

  const formatDate = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? value
      : date.toLocaleString("es-ES");
  };

  return (
    <div className="notif native-alert-bell" ref={containerRef}>
      <button
        type="button"
        className="icon-btn"
        aria-label="Notificaciones de monitorización"
        aria-expanded={open}
        onClick={toggle}
        title="Notificaciones de monitorización"
      >
        <span aria-hidden="true">◔</span>

        {unread > 0 && (
          <span className="notif-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>

      {open && (
        <div className="popover">
          <div className="topbar-popover-head">
            <strong>Alertas de monitorización</strong>

            <button
              className="button"
              type="button"
              disabled={loading}
              onClick={async () => {
                await loadNotifications();
                await refresh();
              }}
            >
              Actualizar
            </button>
          </div>

          {error && <p className="error">{error}</p>}

          {loading ? (
            <p>Cargando notificaciones...</p>
          ) : error || !loaded ? (
            <p>No se ha podido obtener el estado de las notificaciones.</p>
          ) : notifications.length === 0 ? (
            <p>No tienes notificaciones nuevas.</p>
          ) : (
            <div className="native-alert-bell-list">
              {notifications.map(notification => (
                <div
                  key={notification.event_id}
                  className={`native-alert-bell-item ${notification.is_read ? "is-read" : "is-unread"}`}
                >
                  <strong>
                    {notification.severity === "CRITICAL" ? "🔴" : "🟠"}
                    {" "}
                    {alertLabel(notification.alert_type)}
                  </strong>

                  <div>
                    <strong>
                      {notification.client_name ||
                        `Cliente #${notification.client_id}`}
                    </strong>
                    {" · "}
                    {notification.device_name || notification.agent_id}
                  </div>

                  <div>{notification.message}</div>

                  <small>
                    {notification.is_read ? "Leída · " : "Nueva · "}
                    {notification.event_type === "ESCALATE"
                      ? "Aumento de gravedad"
                      : "Nueva incidencia"}
                    {" · "}
                    {formatDate(notification.created_at)}
                  </small>

                  <div>
                    {!notification.is_read && (
                      <button type="button"
                        className="button secondary small"
                        onClick={() => markRead(notification)}>
                        Marcar como leída
                      </button>
                    )}

                    <button
                      type="button"
                      className="button secondary small"
                      onClick={async () => {
                        try {
                          if (notification.agent_family === "windows" &&
                              notification.device_id != null) {
                            await onSelectEquipment(
                              notification.client_id,
                              notification.device_id
                            );
                          } else {
                            await onSelectClient(notification.client_id);
                          }
                          setOpen(false);
                        } catch (err) {
                          setError("No se pudo abrir el dispositivo");
                        }
                      }}
                    >
                      {notification.agent_family === "windows" &&
                       notification.device_id != null
                        ? "Ver equipo"
                        : "Ver cliente"}
                    </button>

                    {notification.aviso_id != null ? (
                      <button
                        type="button"
                        className="button primary small"
                        onClick={() => {
                          onViewAviso(notification);
                          setOpen(false);
                        }}
                      >
                        Ver aviso
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="button primary small"
                        onClick={() => {
                          onCreateAviso(notification);
                          setOpen(false);
                        }}
                      >
                        Crear aviso
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
